// Shapes returned by app/api/pricing.py (app/services/quotations.py serialisers). Money is a
// decimal STRING, never a JS number: it is displayed, compared and sent back as printed.

export type QuoteStatus = 'extracting' | 'needs_review' | 'approved' | 'failed' | 'password_protected' | 'malformed';
export type ReviewState = 'proposed' | 'accepted' | 'corrected' | 'excluded' | 'unknown';
export type EvidenceType = 'supplier_quotation' | 'published_price' | 'advertised_starting_price' | 'user_note';
export type BBox = [number, number, number, number];

export interface QuotationSummary {
  id: string;
  filename: string;
  sha256: string;
  bytes: number;
  page_count: number | null;
  status: QuoteStatus;
  status_detail: string | null;
  evidence_type: EvidenceType;
  supplier: string | null;
  reference: string | null;
  issue_date: string | null;
  currency: string | null;
  total: string | null;
  latest_extraction: number;
  applied_extraction: number | null;
  approved_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  version: number;
  pending: { total: number; fields?: number; lines?: number; conditions?: number } | null;
}

export interface QuoteField {
  id: string;
  key: string;
  value: string | null;
  label: string | null;
  original: string | null;
  page: number | null;
  bbox: BBox | null;
  excerpt: string | null;
  flags: string[];
  confidence: string | null;
  method: string;
  review_state: ReviewState;
  extraction_version: number | null;
  newer_proposal: string | null;
  reviewed_at: string | null;
}

export interface QuoteLine {
  id: string;
  position: number;
  label_original: string;
  category: string | null;
  quantity: string | null;
  unit: string | null;
  unit_price: string | null;
  line_total: string | null;
  currency: string | null;
  is_optional: boolean;
  is_bundle: boolean;
  bundle_components: string[];
  revisions_included: number | null;
  resolution: string | null;
  duration: string | null;
  scope_notes: string | null;
  original: Record<string, string | string[]>;
  page: number | null;
  bbox: BBox | null;
  excerpt: string | null;
  flags: string[];
  method: string;
  review_state: ReviewState;
  extraction_version: number | null;
}

export interface QuoteCondition {
  id: string;
  kind: string;
  text: string;
  value: Record<string, string | number> | null;
  page: number | null;
  bbox: BBox | null;
  method: string;
  review_state: ReviewState;
  extraction_version: number | null;
}

export interface Check {
  key: string;
  label: string;
  status: 'ok' | 'mismatch' | 'not_checkable';
  expected: string | null;
  found: string | null;
  difference?: string | null;
  note: string | null;
  line_id?: string;
}

export interface QuotationDetail extends QuotationSummary {
  supplier_studio: { id: string; name: string | null } | null;
  fields: QuoteField[];
  field_keys: string[];
  lines: QuoteLine[];
  conditions: QuoteCondition[];
  condition_kinds: string[];
  pages: { page: number; width_pt: number; height_pt: number; method: string; chars: number; ocr_confidence: number | null; note: string | null }[];
  reconciliation: { status: 'consistent' | 'inconsistent' | 'incomplete'; checks: Check[]; lines_sum: string | null; optional_sum: string | null };
  extractions: { version: number; extractor: string; created_at: string | null; job_id: string | null; applied: Record<string, unknown> | null; warnings: string[]; lines: number }[];
  job: { id: string; status: string; error: string | null; progress: Record<string, unknown> } | null;
}

export interface ComparisonSample {
  quotation_id: string;
  line_id: string;
  supplier: string | null;
  supplier_studio_id: string | null;
  evidence_type: EvidenceType;
  issue_date: string | null;
  label: string;
  quantity: string | null;
  unit: string | null;
  line_total: string;
  printed_unit_price: string | null;
  derived_unit_price: string | null;
  is_bundle: boolean;
  bundle_components: string[];
  is_optional: boolean;
  revisions_included: number | null;
  resolution: string | null;
  duration: string | null;
  tax_treatment: string | null;
  page: number | null;
  line_total_eur: string | null;
}

export interface ComparisonGroup {
  category: string;
  currency: string;
  samples: ComparisonSample[];
  sample_count: number;
  quotation_count: number;
  supplier_count: number;
  line_total_range: [string, string] | null;
  unit_price_range: [string, string] | null;
  unit_price_count: number;
  conversion: { to: string; eur_per_unit: string; as_of: string | null; source: string } | null;
}

export interface Comparisons {
  groups: ComparisonGroup[];
  categories: string[];
  include_optional: boolean;
}

export interface ExchangeRate {
  currency: string;
  eur_per_unit: string;
  as_of: string;
  source: string;
}

export interface PricingSettings {
  editor_access: boolean;
  ocr_languages: string;
  max_pages: number;
  exchange_rates: ExchangeRate[];
}

export const QUOTE_STATUS_LABEL: Record<QuoteStatus, string> = {
  extracting: 'Extracting',
  needs_review: 'Needs review',
  approved: 'Approved',
  failed: 'Failed',
  password_protected: 'Password-protected',
  malformed: 'Unreadable file',
};

export const EVIDENCE_LABEL: Record<EvidenceType, string> = {
  supplier_quotation: 'Supplier quotation',
  published_price: 'Published price',
  advertised_starting_price: 'Advertised starting price',
  user_note: 'User note',
};

export const FIELD_LABEL: Record<string, string> = {
  supplier: 'Supplier (as printed)',
  supplier_studio: 'Supplier in the directory',
  client: 'Client',
  reference: 'Reference',
  issue_date: 'Issue date',
  expiry_date: 'Expiry date',
  language: 'Language',
  currency: 'Currency',
  tax_treatment: 'Tax treatment',
  subtotal: 'Subtotal',
  discount: 'Discount',
  discount_rate: 'Discount rate %',
  net: 'Net after discount',
  tax_rate: 'Tax rate %',
  tax: 'Tax',
  total: 'Total',
  payment_terms: 'Payment terms',
};

export const FLAG_LABEL: Record<string, string> = {
  ambiguous_separator: 'ambiguous separator',
  currency_unknown: 'currency unknown',
  currency_differs: 'other currency',
  mixed_currencies: 'mixed currencies',
  multiple_candidates: 'several candidates',
  date_order_assumed: 'day/month order assumed',
  position_guess: 'guessed from position',
  matched_by_domain: 'matched by domain',
  optional: 'optional',
  bundle: 'bundle',
  line_arithmetic_mismatch: 'qty × price ≠ total',
  included_no_charge: 'included / no charge',
  negative_amount: 'negative (discount?)',
  extra_columns: 'extra columns',
  no_tax_line_found: 'no tax line',
};

/** Flags that need a person's attention before a value can be trusted. */
export const ATTENTION_FLAGS = new Set([
  'ambiguous_separator',
  'currency_unknown',
  'currency_differs',
  'mixed_currencies',
  'multiple_candidates',
  'line_arithmetic_mismatch',
  'negative_amount',
  'extra_columns',
]);

export const CONDITION_LABEL: Record<string, string> = {
  validity: 'Validity',
  deposit: 'Deposit',
  payment_milestone: 'Payment milestones',
  revision_rounds: 'Revision rounds',
  modeling_inputs: 'Modelling inputs',
  source_files: 'Source files',
  licensing: 'Licensing / usage',
  hosting_duration: 'Hosting duration',
  rush_fee: 'Rush fee',
  additional_work_rate: 'Additional-work rate',
  deliverable_formats: 'Deliverable formats',
  delivery: 'Delivery time',
  other: 'Other',
};

export const PAGE_METHOD_LABEL: Record<string, string> = {
  text: 'text layer',
  ocr: 'OCR',
  ocr_unavailable: 'OCR not installed',
  ocr_failed: 'OCR failed',
  empty: 'empty',
};

/** Display a decimal string with thin grouping, never through a float. */
export function money(v: string | null | undefined, currency?: string | null): string {
  if (v === null || v === undefined || v === '') return '—';
  const neg = v.startsWith('-');
  const [int, dec] = (neg ? v.slice(1) : v).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${neg ? '−' : ''}${grouped}${dec !== undefined ? `.${dec}` : ''}${currency ? ` ${currency}` : ''}`;
}
